import { useState } from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import { Checkbox, Button } from '@mesh.ai/rabbit';
import { useToast } from 'components/hooks/useToast';
import { useHistory } from 'react-router-dom';
import {
  moveToNextQuestionAction,
  moveToPreviousQuestionAction,
  moveToQuestion,
} from 'screens/Engagement/pulseSurveyActions';

import { ENGAGEMENT_ANSWER_TYPES } from 'constants/ProjectConstants';
import { AnonymousIcon } from 'constants/icons';
import {
  sendClickedBackEngagementSurveyEvent,
  sendClickedNextEngagementSurveyEvent,
  sendSubmittedSurveyFormEvent,
} from 'constants/mixpanelHelper';
import { routes } from 'constants/routes';

import { submitSurvey } from 'services/engagement';

import { QuestionStepper } from 'uiToolkit/QuestionStepper';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

type QuestionsData = {
  id: number;
  isRequired: boolean;
  isTextRequired: boolean;
  isVisited: boolean;
  selectedAnswerOptions: [];
  feedbackText: string;
  answerType: string;
}[];

const getStepperData = (questionsData: QuestionsData) =>
  questionsData.map(
    ({ id, isRequired, isTextRequired, isVisited, selectedAnswerOptions, feedbackText, answerType }) => {
      let isAnswered = true;

      if (isRequired || isTextRequired) {
        if (answerType === ENGAGEMENT_ANSWER_TYPES.NONE) isAnswered = feedbackText !== null;
        else if (isTextRequired)
          isAnswered = isRequired ? selectedAnswerOptions.length !== 0 && feedbackText !== null : feedbackText !== null;
        else isAnswered = isRequired && selectedAnswerOptions.length !== 0;
      }

      return {
        id,
        isVisited,
        isRequired: isRequired || isTextRequired,
        isAnswered,
      };
    }
  );

type SurveyFormFooterProps = {
  isPreview: boolean;
  surveyId: string;
  questionsData: QuestionsData;
  currentQuestionId: number;
  isAnonymous: boolean;
  remainingRequiredQuestionCount: number;
  dispatch: (arg: unknown) => void;
};

export const SurveyFormFooter = ({
  isPreview,
  surveyId,
  questionsData,
  currentQuestionId,
  isAnonymous,
  remainingRequiredQuestionCount,
  dispatch,
}: SurveyFormFooterProps) => {
  const history = useHistory();
  const toast = useToast();
  const theme = useTheme();

  const [acceptSubmission, setAcceptSubmission] = useState(false);
  const isFirstQuestion = currentQuestionId === questionsData?.[0].id;
  const isLastQuestion = currentQuestionId === questionsData?.[questionsData.length - 1].id;
  const submissionEnabled = remainingRequiredQuestionCount === 0 && acceptSubmission;

  const handleSubmitSurvey = () => {
    if (isLastQuestion) {
      // We call api only when it's not in preview mode.
      if (isPreview) {
        toast({ title: 'This can not be submitted as it is a preview.', status: 'error' });
      } else {
        submitSurvey({ surveyId })
          .then(() => {
            toast({ title: 'Survey submitted successfully', status: 'success' });

            // Send tracking Events
            sendSubmittedSurveyFormEvent({
              eventProperties: {
                surveyId,
                success: true,
              },
            });

            history.push(routes.engagement.index);
          })
          .catch(() => {
            toast({ title: 'Error submitting survey', status: 'error' });
            sendSubmittedSurveyFormEvent({
              eventProperties: {
                surveyId,
                success: false,
              },
            });
          });
      }
    } else {
      dispatch(moveToNextQuestionAction());
      sendClickedNextEngagementSurveyEvent();
    }
  };

  return (
    <Flex w="100%" flexDir="column">
      {isLastQuestion && (
        <Flex
          h="44px"
          w="100%"
          gap="8px"
          alignItems="center"
          px="16px"
          boxShadow="0px -1px 2px 0px rgba(0, 0, 0, 0.08)"
        >
          <Checkbox
            id="pulse_submit_checkbox"
            checked={acceptSubmission}
            color="primary"
            onCheckedChange={() => setAcceptSubmission((val) => !val)}
          />
          <Box as="label" htmlFor="pulse_submit_checkbox">
            <TextBodyMedium userSelect="none">Are you sure you want to submit your response?</TextBodyMedium>
          </Box>
        </Flex>
      )}
      {isAnonymous && (
        <Flex h="40px" alignItems="center" bg="neutral.EBEFF3" px="16px" gap="8px">
          {/* @ts-expect-error ignore */}
          <AnonymousIcon color={theme.colors.text.g2} size="tiny" />
          <TextLabel color="text.g2">You are anonymous</TextLabel>
        </Flex>
      )}
      <Flex h="75px" w="100%" alignItems="center" justifyContent="space-between" bg="neutral.F5F7F9" px="16px">
        <Button
          variant="transparentOutline"
          style={{ display: isFirstQuestion ? 'none' : undefined }}
          onClick={() => {
            sendClickedBackEngagementSurveyEvent();
            dispatch(moveToPreviousQuestionAction());
          }}
        >
          Back
        </Button>
        <Flex gap="16px" alignItems="center">
          <Flex flexDir="column" alignItems="end">
            <TextLabel color="text.g2" style={{ textAlign: 'left' }}>
              {remainingRequiredQuestionCount} question
              {remainingRequiredQuestionCount > 1 ? 's' : ''} remaining
            </TextLabel>
            <QuestionStepper
              currentItemId={currentQuestionId}
              data={getStepperData(questionsData)}
              clickHandler={(id: number) => dispatch(moveToQuestion(id))}
              wrapperProps={{ style: { justifyContent: 'end' } }}
            />
          </Flex>
          <Button
            disabled={(isLastQuestion && !submissionEnabled) || (isLastQuestion && isPreview)}
            onClick={handleSubmitSurvey}
          >
            {isLastQuestion ? 'Submit' : 'Next'}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
